import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Award, AlertCircle, Download, ArrowLeft, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';
import { goBack } from '../../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EmitirGeneralPage() {
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [emittedCurso, setEmittedCurso] = useState(null);
  
  const [tipo, setTipo] = useState('constancia');
  const [cursoId, setCursoId] = useState('');
  const [dni, setDni] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    fetchCursos();
  }, []);

  const fetchCursos = async () => {
    try {
      const res = await axios.get(`${API_URL}/capacitaciones/cursos`);
      setCursos(res.data);
    } catch (err) {
      console.error('Error cargando cursos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cursoId) {
      setError('Seleccioná un curso');
      return;
    }
    if (!dni.trim() || !email.trim()) {
      setError('Completá todos los campos');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    setPdfUrl(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/capacitaciones/cursos/${cursoId}/emitir/${tipo}`,
        { dni, email },
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setEmittedCurso(cursos.find(c => c.id === cursoId));
      
    } catch (err) {
      console.error('Error emitiendo documento:', err);
      
      if (err.response?.status === 404) {
        setError('No se encontró una inscripción con esos datos. Verificá tu DNI, email y que hayas seleccionado el curso correcto.');
      } else if (err.response?.status === 400) {
        try {
          if (err.response.data instanceof Blob) {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            setError(json.detail || 'Tu asistencia aún no fue registrada. Contactá al administrador.');
          } else if (typeof err.response.data === 'object') {
            setError(err.response.data.detail || 'Tu asistencia aún no fue registrada. Contactá al administrador.');
          } else {
            setError('Tu asistencia aún no fue registrada. Contactá al administrador.');
          }
        } catch {
          setError('Tu asistencia aún no fue registrada. Contactá al administrador.');
        }
      } else {
        setError('Error al generar el documento. Intentá nuevamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    if (pdfUrl && emittedCurso) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${tipo}_${emittedCurso.nombre.replace(/\s+/g, '_')}.pdf`;
      link.click();
    }
  };

  const resetForm = () => {
    setPdfUrl(null);
    setEmittedCurso(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
            <button
              onClick={() => goBack()}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-lg mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <FileText className="w-8 h-8 text-blue-600" />
                <Award className="w-8 h-8 text-amber-600" />
              </div>
              Emitir Constancia o Certificado
            </CardTitle>
            <p className="text-center text-gray-600 text-sm">
              Ingresá tus datos para descargar tu documento
            </p>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {pdfUrl ? (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <Download className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">¡Documento generado!</h3>
                <p className="text-gray-600 text-sm">
                  Tu {tipo === 'constancia' ? 'constancia de asistencia' : 'certificado de capacitación'} está lista.
                </p>
                {emittedCurso && (
                  <p className="text-sm text-gray-500">{emittedCurso.nombre}</p>
                )}
                <Button
                  onClick={handleDownload}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={resetForm}
                  className="w-full"
                >
                  Emitir otro documento
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Tipo selector */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setTipo('constancia')}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      tipo === 'constancia'
                        ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                    }`}
                  >
                    <FileText className="w-4 h-4 inline mr-2" />
                    Constancia
                  </button>
                  <button
                    type="button"
                    onClick={() => setTipo('certificado')}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      tipo === 'certificado'
                        ? 'bg-amber-100 text-amber-700 border-2 border-amber-500'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                    }`}
                  >
                    <Award className="w-4 h-4 inline mr-2" />
                    Certificado
                  </button>
                </div>

                <div>
                  <Label htmlFor="curso">Curso *</Label>
                  <select
                    id="curso"
                    value={cursoId}
                    onChange={(e) => setCursoId(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    disabled={loading}
                  >
                    <option value="">Seleccioná el curso...</option>
                    {cursos.map((curso) => (
                      <option key={curso.id} value={curso.id}>
                        {curso.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="dni">DNI *</Label>
                  <Input
                    id="dni"
                    value={dni}
                    onChange={(e) => setDni(e.target.value)}
                    placeholder="Ingresá tu DNI"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ingresá tu email"
                  />
                </div>

                <Button
                  type="submit"
                  className={`w-full ${tipo === 'constancia' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                  disabled={submitting || loading}
                >
                  {submitting ? 'Generando...' : `Emitir ${tipo === 'constancia' ? 'Constancia' : 'Certificado'}`}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-4 border-t">
              <p className="text-xs text-gray-500 text-center">
                Solo podés emitir documentos si tu asistencia fue registrada por el administrador.
                Los documentos incluyen un código QR para verificar su autenticidad.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
